
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

const AirOtomatikYanitPage = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const CodeBlock = ({ title, code }: { title: string; code: string }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
      try {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Copy failed', err);
      }
    };
    return (
      <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-400 text-sm">{title}</span>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            </div>
            <span>{copied ? 'Kopyalandı!' : 'Kopyala'}</span>
          </button>
        </div>
        <pre className="text-sm text-gray-100 whitespace-pre-wrap">{code}</pre>
      </div>
    );
  };

  const airCapabilities = [
    {
      icon: 'ri-robot-line',
      title: 'Otomatik Soruşturma',
      description:
        'Bir uyarı tetiklendiğinde AI, ilgili tüm varlıkları (dosyalar, süreçler, ağ bağlantıları) otomatik olarak analiz eder.',
      color: 'from-[#0078D4] to-[#005A9E]',
    },
    {
      icon: 'ri-shield-flash-line',
      title: 'Otomatik Düzeltme',
      description:
        'Tespit edilen tehditlere karşı dosya karantinası, süreç sonlandırma ve cihaz izolasyonu gibi eylemler otomatik uygulanır.',
      color: 'from-[#005A9E] to-[#003D6B]',
    },
    {
      icon: 'ri-time-line',
      title: 'Hızlı Müdahale',
      description:
        'İnsan müdahalesi gerektirmeden saniyeler içinde yanıt vererek saldırı zincirini kırar ve yayılmayı önler.',
      color: 'from-[#00BCF2] to-[#0078D4]',
    },
    {
      icon: 'ri-file-search-line',
      title: 'Şeffaf Raporlama',
      description:
        'Her soruşturma adımı kayıt altına alınır; analist tam olarak neyin neden yapıldığını görebilir.',
      color: 'from-[#003D6B] to-[#002050]',
    },
  ];

  const automationLevels = [
    {
      level: 'Seviye 1',
      name: 'No automation',
      description:
        'Soruşturma başlatılır ancak hiçbir düzeltme eylemi otomatik uygulanmaz. Tüm eylemler manuel onay gerektirir.',
      icon: 'ri-user-line',
      color: 'bg-gray-500',
      badge: 'bg-gray-100 text-gray-700',
      recommended: false,
      pros: ['Tam kontrol', 'Sıfır false positive riski'],
      cons: ['Yavaş yanıt', 'Yüksek analist iş yükü', 'Saldırı yayılabilir'],
    },
    {
      level: 'Seviye 2',
      name: 'Semi - Require approval for core folders',
      description:
        'Sistem klasörleri (Windows, Program Files) dışındaki düzeltmeler otomatik uygulanır. Kritik klasörler için onay gerekir.',
      icon: 'ri-shield-half-line',
      color: 'bg-amber-500',
      badge: 'bg-amber-100 text-amber-700',
      recommended: false,
      pros: ['Kısmi otomasyon', 'Kritik klasörler korumalı'],
      cons: ['Karmaşık yapılandırma', 'Hala gecikme riski'],
    },
    {
      level: 'Seviye 3',
      name: 'Semi - Require approval for non-temp folders',
      description:
        'Geçici klasörler dışındaki tüm düzeltmeler onay gerektirir. Temp klasörlerindeki tehditler otomatik temizlenir.',
      icon: 'ri-shield-check-line',
      color: 'bg-[#0078D4]',
      badge: 'bg-[#0078D4]/10 text-[#0078D4]',
      recommended: false,
      pros: ['Dengeli yaklaşım', 'Temp tehditleri hızlı temizlenir'],
      cons: ['Çoğu eylem hala manuel'],
    },
    {
      level: 'Seviye 4',
      name: 'Semi - Require approval for any remediation',
      description:
        'Soruşturma tamamen otomatik yapılır ancak her düzeltme eylemi için analist onayı gerekir.',
      icon: 'ri-shield-star-line',
      color: 'bg-[#005A9E]',
      badge: 'bg-[#005A9E]/10 text-[#005A9E]',
      recommended: false,
      pros: ['Tam soruşturma otomasyonu', 'Kontrollü düzeltme'],
      cons: ['Düzeltme hala gecikebilir'],
    },
    {
      level: 'Seviye 5',
      name: 'Full - Remediate threats automatically',
      description:
        'Hem soruşturma hem de düzeltme tamamen otomatiktir. Yüksek güven skorlu tehditler anında temizlenir.',
      icon: 'ri-rocket-line',
      color: 'bg-green-600',
      badge: 'bg-green-100 text-green-700',
      recommended: true,
      pros: ['En hızlı yanıt', 'Minimum analist yükü', 'Saldırı zinciri anında kırılır'],
      cons: ['Nadiren false positive riski'],
    },
  ];

  const investigationSteps = [
    {
      step: 1,
      title: 'Tetikleme',
      description:
        'Bir MDE uyarısı veya analist tarafından manuel başlatma ile soruşturma süreci başlar.',
      icon: 'ri-alarm-warning-line',
      color: 'bg-[#0078D4]',
      details: [
        'Yüksek/Orta öncelikli uyarılar otomatik tetikler',
        'Analist herhangi bir uyarıdan manuel başlatabilir',
        'Scheduled hunting sorguları da tetikleyebilir',
      ],
    },
    {
      step: 2,
      title: 'Kapsam Belirleme',
      description:
        'AI, uyarıyla ilişkili tüm varlıkları (cihazlar, kullanıcılar, dosyalar, IP\'ler) tespit eder.',
      icon: 'ri-search-eye-line',
      color: 'bg-[#00BCF2]',
      details: [
        'İlgili cihazlar ve kullanıcılar listelenir',
        'Şüpheli dosya ve süreçler belirlenir',
        'Ağ bağlantıları ve IP adresleri analiz edilir',
      ],
    },
    {
      step: 3,
      title: 'Veri Toplama',
      description:
        'Her varlık için derinlemesine veri toplanır: dosya hash\'leri, süreç ağaçları, ağ trafiği.',
      icon: 'ri-database-2-line',
      color: 'bg-[#005A9E]',
      details: [
        'Dosya hash\'leri VirusTotal ile karşılaştırılır',
        'Süreç ağacı (process tree) oluşturulur',
        'Registry değişiklikleri kaydedilir',
      ],
    },
    {
      step: 4,
      title: 'Tehdit Analizi',
      description:
        'Toplanan veriler AI algoritmaları ile analiz edilir; tehdit sınıflandırılır ve güven skoru atanır.',
      icon: 'ri-cpu-line',
      color: 'bg-[#003D6B]',
      details: [
        'MITRE ATT&CK teknikleri haritalandırılır',
        'Güven skoru (High/Medium/Low) atanır',
        'Benzer geçmiş olaylarla ilişkilendirilir',
      ],
    },
    {
      step: 5,
      title: 'Karar',
      description:
        'Otomasyon seviyesine göre eylemler otomatik uygulanır veya analist onayına sunulur.',
      icon: 'ri-checkbox-circle-line',
      color: 'bg-[#002050]',
      details: [
        "Seviye 5'te otomatik onay",
        "Düşük seviyelerde Action Center'a gönderilir",
        'Analist eylemi onaylayabilir veya reddedebilir',
      ],
    },
    {
      step: 6,
      title: 'Düzeltme',
      description:
        'Onaylanan eylemler uygulanır: karantina, izolasyon, süreç sonlandırma vb.',
      icon: 'ri-shield-flash-line',
      color: 'bg-green-600',
      details: [
        'Zararlı dosyalar karantinaya alınır',
        'Kötü amaçlı süreçler sonlandırılır',
        'Gerekirse cihaz ağdan izole edilir',
      ],
    },
  ];

  const remediationActions = [
    {
      category: 'Dosya İşlemleri',
      icon: 'ri-file-damage-line',
      color: 'text-red-600 bg-red-50',
      actions: [
        { name: 'Dosya Karantinası', desc: 'Zararlı dosyayı güvenli karantina alanına taşır' },
        { name: 'Dosya Silme', desc: 'Yüksek güven skorlu tehditleri kalıcı olarak siler' },
        { name: 'Dosya Engelleme', desc: 'Belirli hash değerli dosyaların çalışmasını engeller' },
      ],
    },
    {
      category: 'Süreç İşlemleri',
      icon: 'ri-terminal-box-line',
      color: 'text-[#0078D4] bg-[#0078D4]/10',
      actions: [
        { name: 'Süreç Sonlandırma', desc: 'Kötü amaçlı süreçleri anında durdurur' },
        { name: 'Süreç Engelleme', desc: 'Belirli uygulamaların başlatılmasını önler' },
        { name: 'Servis Durdurma', desc: 'Zararlı Windows servislerini devre dışı bırakır' },
      ],
    },
    {
      category: 'Cihaz İşlemleri',
      icon: 'ri-computer-line',
      color: 'text-[#005A9E] bg-[#005A9E]/10',
      actions: [
        { name: 'Cihaz İzolasyonu', desc: 'Cihazı ağdan keser, sadece MDE iletişimine izin verir' },
        { name: 'Soruşturma Paketi', desc: 'Forensic analiz için cihazdan veri toplar' },
        { name: 'Antivirüs Taraması', desc: 'Tam sistem taraması başlatır' },
      ],
    },
    {
      category: 'Kimlik İşlemleri',
      icon: 'ri-user-forbid-line',
      color: 'text-amber-600 bg-amber-50',
      actions: [
        { name: 'Kullanıcı Engelleme', desc: 'Ele geçirilmiş hesabı geçici olarak devre dışı bırakır' },
        { name: 'Oturum Sonlandırma', desc: 'Aktif kullanıcı oturumlarını zorla kapatır' },
        { name: 'Parola Sıfırlama', desc: 'Kullanıcıya parola sıfırlama bildirimi gönderir' },
      ],
    },
  ];

  const faqs = [
    {
      q: 'AIR, false positive durumunda yanlışlıkla meşru dosyaları siler mi?',
      a: "AIR, yüksek güven skoru olmayan durumlarda otomatik silme yapmaz; bunun yerine karantinaya alır. Karantinaya alınan dosyalar 30 gün boyunca geri yüklenebilir. Ayrıca Seviye 5'te bile, Microsoft'un tehdit istihbaratı tarafından doğrulanmamış dosyalar için onay mekanizması devreye girer.",
    },
    {
      q: 'Cihaz izolasyonu uygulandığında kullanıcı ne görür?',
      a: 'İzole edilen cihazda kullanıcı tüm ağ bağlantısını kaybeder. Ancak MDE portalı ile iletişim devam eder. Kullanıcıya ekranda "Bu cihaz güvenlik nedeniyle izole edilmiştir" mesajı gösterilir. IT ekibi izolasyonu kaldırana kadar cihaz internete erişemez.',
    },
    {
      q: 'AIR ile manuel soruşturma arasındaki fark nedir?',
      a: "Manuel soruşturma, bir analistin tüm varlıkları tek tek incelemesini gerektirir ve saatler alabilir. AIR, aynı süreci dakikalar içinde tamamlar ve tüm adımları otomatik olarak belgeler. Analist, AIR'ın hazırladığı soruşturma raporunu inceleyerek onay veya ret kararı verebilir.",
    },
    {
      q: 'Seviye 5 otomasyon tüm ortamlar için uygun mu?',
      a: 'Seviye 5, olgun güvenlik ekipleri ve iyi yapılandırılmış ortamlar için önerilir. Kritik üretim sunucuları veya OT/ICS sistemleri için daha düşük otomasyon seviyeleri tercih edilebilir. Bu sistemler için ayrı cihaz grupları oluşturarak farklı otomasyon seviyeleri uygulayabilirsiniz.',
    },
    {
      q: 'AIR tarafından alınan eylemleri geri alabilir miyim?',
      a: 'Evet. Action Center üzerinden tüm AIR eylemlerini görebilir ve geri alabilirsiniz. Karantinaya alınan dosyalar geri yüklenebilir, izole edilen cihazlar serbest bırakılabilir. Ancak silinen dosyalar geri alınamaz; bu nedenle AIR varsayılan olarak silme yerine karantinayı tercih eder.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0078D4] to-[#005A9E] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white text-sm font-medium mb-8 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span>EDR Sayfasına Dön</span>
            </Link>

            <div className="flex items-center space-x-3 mb-6 flex-wrap gap-y-2">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Otomatik Yanıt
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Microsoft Defender XDR
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Otomatik Soruşturma ve Yanıt (AIR) ile Hızlı Müdahale
            </h1>

            <p className="text-lg text-white/85 leading-relaxed mb-8 max-w-3xl">
              Automated Investigation and Response özelliklerini kullanarak güvenlik
              olaylarına hızlı ve etkili müdahale edin; saldırı zincirini saniyeler içinde kırın.
            </p>

            <div className="flex items-center space-x-6 text-white/70 text-sm flex-wrap gap-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </div>
                <span>8 Aralık 2025</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </div>
                <span>10 dk okuma</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-base"></i>
                </div>
                <span>Özgür Çebi</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Makale İçeriği */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="text-gray-700 leading-relaxed space-y-6">
              {/* Giriş */}
              <p className="text-lg">
                Modern SOC ekipleri her gün yüzlerce güvenlik uyarısıyla karşılaşır. Her uyarıyı manuel
                olarak soruşturmak hem zaman alıcı hem de insan hatasına açıktır. Microsoft Defender
                for Endpoint&apos;in <strong>Automated Investigation and Response (AIR)</strong>{' '}
                özelliği, bu sorunu kökten çözer: bir tehdit tespit edildiğinde yapay zeka devreye
                girer, soruşturmayı otomatik yürütür ve gerekli düzeltme eylemlerini uygular.
              </p>

              {/* AIR Nedir? */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">AIR Nedir ve Neden Önemlidir?</h2>
                <p className="mb-6">
                  AIR, Microsoft&apos;un güvenlik analistlerinin soruşturma süreçlerini inceleyerek
                  geliştirdiği bir otomasyon motorudur. Bir analistin manuel olarak yapacağı her adımı
                  — varlık toplama, tehdit analizi, karar verme, düzeltme — otomatik olarak
                  gerçekleştirir.
                </p>

                <div className="grid md:grid-cols-2 gap-6 my-8">
                  {airCapabilities.map((cap, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${cap.color} rounded-xl flex items-center justify-center mb-4`}>
                        <i className={`${cap.icon} text-3xl text-white`}></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{cap.title}</h3>
                      <p className="text-sm text-gray-600">{cap.description}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">AIR Olmadan vs. AIR ile</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-5 border border-red-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-close-circle-line text-red-600 text-lg"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">Manuel Soruşturma</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-time-line text-red-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>
                            Ortalama yanıt süresi: <strong>4-8 saat</strong>
                          </span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-user-line text-red-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Deneyimli analist gerektirir</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-error-warning-line text-red-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>İnsan hatası riski yüksek</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-moon-line text-red-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Gece/hafta sonu uyarıları gecikir</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-check-double-line text-green-600 text-lg"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">AIR ile Otomatik</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-flashlight-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>
                            Ortalama yanıt süresi: <strong>2-5 dakika</strong>
                          </span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-robot-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>7/24 kesintisiz çalışır</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-file-list-3-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Her adım otomatik belgelenir</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-scales-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Tutarlı ve önyargısız analiz</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Soruşturma Süreci */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">AIR Soruşturma Süreci: Adım Adım</h2>
                <p className="mb-8">
                  Bir uyarı tetiklendiğinden düzeltme eylemine kadar geçen süreçte AIR altı temel
                  adımı otomatik olarak yürütür.
                </p>

                <div className="space-y-6">
                  {investigationSteps.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex flex-col items-center mr-6">
                        <div className={`w-14 h-14 ${item.color} text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                          <i className={`${item.icon} text-2xl`}></i>
                        </div>
                        {index < investigationSteps.length - 1 && (
                          <div className="w-0.5 h-12 bg-gray-200 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            Adım {item.step}
                          </span>
                          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <ul className="space-y-1.5">
                            {item.details.map((detail, di) => (
                              <li key={di} className="text-sm text-gray-600 flex items-start">
                                <i className="ri-arrow-right-s-line text-[#0078D4] mr-1.5 mt-0.5 flex-shrink-0"></i>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Otomasyon Seviyeleri */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Otomasyon Seviyeleri: Hangisini Seçmeli?</h2>
                <p className="mb-8">
                  MDE, 1&apos;den 5&apos;e kadar beş farklı otomasyon seviyesi sunar. Seviye ne kadar
                  yüksekse, o kadar az insan müdahalesi gerekir. Doğru seviyeyi seçmek, organizasyonunuzun
                  olgunluk düzeyine ve risk toleransına bağlıdır.
                </p>

                <div className="space-y-4">
                  {automationLevels.map((level, index) => (
                    <div
                      key={index}
                      className={`bg-white border-2 rounded-xl p-6 transition-all ${
                        level.recommended ? 'border-green-400 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      {level.recommended && (
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full whitespace-nowrap">
                            ✓ Olgun Ortamlar İçin Önerilen
                          </span>
                        </div>
                      )}
                      <div className="flex items-start">
                        <div className={`w-12 h-12 ${level.color} text-white rounded-xl flex items-center justify-center mr-4 flex-shrink-0`}>
                          <i className={`${level.icon} text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1 flex-wrap gap-y-1">
                            <h3 className="text-lg font-bold text-gray-900">{level.level}</h3>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${level.badge}`}>
                              {level.name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{level.description}</p>
                          <div className="grid md:grid-cols-2 gap-3">
                            <div className="bg-green-50 rounded-lg p-3">
                              <h4 className="text-xs font-bold text-green-700 mb-2 uppercase tracking-wide">
                                Avantajlar
                              </h4>
                              <ul className="space-y-1">
                                {level.pros.map((pro, pi) => (
                                  <li key={pi} className="text-xs text-gray-600 flex items-start">
                                    <i className="ri-check-line text-green-600 mr-1.5 mt-0.5 flex-shrink-0"></i>
                                    <span>{pro}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                              <h4 className="text-xs font-bold text-red-700 mb-2 uppercase tracking-wide">
                                Dezavantajlar
                              </h4>
                              <ul className="space-y-1">
                                {level.cons.map((con, ci) => (
                                  <li key={ci} className="text-xs text-gray-600 flex items-start">
                                    <i className="ri-close-line text-red-500 mr-1.5 mt-0.5 flex-shrink-0"></i>
                                    <span>{con}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6 mt-8">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Cihaz Grubu Bazlı Yapılandırma</h4>
                      <p className="text-gray-700 text-sm">
                        Tüm organizasyon için tek bir otomasyon seviyesi kullanmak zorunda değilsiniz.
                        Kritik sunucular için Seviye 3, kullanıcı iş istasyonları için Seviye 5 gibi farklı
                        cihaz gruplarına farklı seviyeler atayabilirsiniz. Bu yaklaşım, hem güvenliği hem
                        de operasyonel esnekliği dengeler.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Düzeltme Eylemleri */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">AIR Düzeltme Eylemleri</h2>
                <p className="mb-6">
                  AIR, tehdidin türüne ve kapsamına göre farklı kategorilerde düzeltme eylemleri
                  uygulayabilir. Her eylem, geri alınabilirlik prensibiyle tasarlanmıştır.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {remediationActions.map((cat, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-center mb-4">
                        <div className={`w-10 h-10 ${cat.color} rounded-lg flex items-center justify-center mr-3`}>
                          <i className={`${cat.icon} text-xl`}></i>
                        </div>
                        <h3 className="text-base font-bold text-gray-900">{cat.category}</h3>
                      </div>
                      <div className="space-y-3">
                        {cat.actions.map((action, ai) => (
                          <div key={ai} className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm font-semibold text-gray-900 mb-0.5">{action.name}</p>
                            <p className="text-xs text-gray-500">{action.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Action Center */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Action Center: Tüm Eylemlerin Merkezi</h2>
                <p className="mb-6">
                  Action Center, AIR tarafından gerçekleştirilen veya onay bekleyen tüm eylemlerin
                  görüntülendiği ve yönetildiği merkezi paneldir.{' '}
                  <strong>security.microsoft.com &gt; Action Center</strong> yolundan erişilir.
                </p>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Action Center Sekmeleri</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-time-line text-amber-600 text-xl"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">Pending (Bekleyen)</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Analist onayı bekleyen eylemler burada listelenir. Her eylem için Approve (Onayla)
                        veya Reject (Reddet) seçeneği sunulur.
                      </p>
                      <div className="bg-amber-50 rounded-lg p-3 text-xs text-gray-600">
                        <strong>İpucu:</strong> Bekleyen eylemleri 7 gün içinde onaylamazsanız otomatik
                        olarak reddedilir.
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-history-line text-green-600 text-xl"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">History (Geçmiş)</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Tamamlanan tüm eylemler burada görüntülenir. Geri alınabilir eylemler için Undo
                        (Geri Al) butonu mevcuttur.
                      </p>
                      <div className="bg-green-50 rounded-lg p-3 text-xs text-gray-600">
                        <strong>İpucu:</strong> Karantinaya alınan dosyalar 30 gün boyunca geri yüklenebilir.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Action Center İzin Gereksinimleri</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        Action Center&apos;da eylemleri onaylamak veya reddetmek için kullanıcının aşağıdaki
                        rollerden birine sahip olması gerekir:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>
                            <strong>Security Administrator:</strong> Tüm eylemleri onaylayabilir
                          </span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>
                            <strong>Security Operator:</strong> Eylemleri onaylayabilir, politika değiştiremez
                          </span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>
                            <strong>Security Reader:</strong> Sadece görüntüleyebilir, onaylayamaz
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Yapılandırma */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">AIR Yapılandırması: Adım Adım</h2>
                <p className="mb-6">
                  AIR&apos;ı etkinleştirmek ve otomasyon seviyesini yapılandırmak için Microsoft Defender
                  portalını kullanın.
                </p>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Portal Üzerinden Yapılandırma</h3>
                  <div className="space-y-5">
                    {[
                      {
                        step: 1,
                        title: 'Defender Portalına Gidin',
                        desc: 'security.microsoft.com adresine gidin ve Settings > Endpoints > Advanced features bölümüne geçin.',
                      },
                      {
                        step: 2,
                        title: "Automated Investigation'ı Etkinleştirin",
                        desc: '"Automated investigation" seçeneğini On konumuna getirin. Bu, AIR motorunu aktif hale getirir.',
                      },
                      {
                        step: 3,
                        title: 'Cihaz Gruplarını Yapılandırın',
                        desc: 'Settings > Endpoints > Device groups bölümünden cihaz gruplarınızı oluşturun veya düzenleyin.',
                      },
                      {
                        step: 4,
                        title: 'Otomasyon Seviyesini Seçin',
                        desc: 'Her cihaz grubu için Automation level açılır menüsünden istediğiniz seviyeyi seçin (Full önerilir).',
                      },
                      {
                        step: 5,
                        title: 'Kaydedin ve Test Edin',
                        desc: 'Değişiklikleri kaydedin. Bir test cihazında simüle edilmiş bir tehdit oluşturarak AIR\'ın çalıştığını doğrulayın.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start">
                        <div className="w-10 h-10 bg-[#0078D4] text-white rounded-lg flex items-center justify-center mr-4 flex-shrink-0 font-bold text-sm">
                          {item.step}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-gray-700 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <CodeBlock
                  title="PowerShell - AIR Durumunu Kontrol Et"
                  code={`# MDE Advanced Features durumunu kontrol et
# Not: Bu ayarlar portal üzerinden yapılır, PowerShell sadece doğrulama için kullanılır

# Defender servis durumunu kontrol et
Get-Service -Name "Sense" | Select-Object Name, Status, StartType

# MDE yapılandırma bilgilerini görüntüle
Get-MpComputerStatus | Select-Object \\\` 
    AMServiceEnabled, \\\` 
    AntispywareEnabled, \\\` 
    BehaviorMonitorEnabled, \\\` 
    IoavProtectionEnabled, \\\` 
    RealTimeProtectionEnabled

# AIR soruşturma geçmişini Event Log'dan çek
Get-WinEvent -LogName "Microsoft-Windows-Windows Defender/Operational" |
    Where-Object { $_.Id -in @(1001, 1002, 1003) } |
    Select-Object TimeCreated, Id, Message |
    Sort-Object TimeCreated -Descending |
    Select-Object -First 20`}
                />
              </section>

              {/* KQL Sorguları */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Hunting ile AIR Analizi</h2>
                <p className="mb-6">
                  AIR soruşturmalarını Advanced Hunting üzerinden KQL sorguları ile analiz edebilirsiniz.
                  Bu, hangi tehditlerin ne sıklıkla tetiklendiğini ve AIR&apos;ın etkinliğini ölçmenizi sağlar.
                </p>

                <div className="space-y-6">
                  <CodeBlock
                    title="KQL - AIR Soruşturma Özeti"
                    code={`// Son 30 günde AIR tarafından başlatılan soruşturmalar
DeviceEvents
| where Timestamp > ago(30d)
| where ActionType == "AntivirusDetection" or ActionType startswith "Air"
| summarize
    ToplamOlay = count(),
    EtkilenenCihaz = dcount(DeviceName)
    by ActionType
| sort by ToplamOlay desc`}
                  />

                  <CodeBlock
                    title="KQL - Otomatik Düzeltme Eylemleri"
                    code={`// AIR tarafından gerçekleştirilen düzeltme eylemlerini listele
DeviceEvents
| where Timestamp > ago(7d)
| where ActionType in (
    "AntivirusQuarantineFile",
    "AntivirusDeleteFile",
    "IsolateDevice",
    "KillProcess"
)
| project
    Timestamp,
    DeviceName,
    ActionType,
    FileName,
    FolderPath,
    InitiatingProcessFileName
| sort by Timestamp desc`}
                  />

                  <CodeBlock
                    title="KQL - False Positive Tespiti"
                    code={`// Karantinaya alınan ve sonradan geri yüklenen dosyalar (potansiyel false positive)
DeviceEvents
| where Timestamp > ago(30d)
| where ActionType == "AntivirusQuarantineFile"
| join kind=leftouter (
    DeviceEvents
    | where ActionType == "AntivirusQuarantineFileRestored"
    | project DeviceName, RestoredFile = FileName, RestoredTime = Timestamp
) on DeviceName
| where isnotempty(RestoredFile)
| project
    QuarantineTime = Timestamp,
    DeviceName,
    FileName,
    RestoredTime
| sort by QuarantineTime desc`}
                  />
                </div>
              </section>

              {/* Sık Sorulan Sorular */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Sık Sorulan Sorular</h2>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-gray-500 text-xl transition-transform`}></i>
                        </div>
                      </button>
                      {expandedFaq === index && (
                        <div className="px-5 pb-5 border-t border-gray-100">
                          <p className="text-sm text-gray-600 leading-relaxed pt-4">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Sonuç */}
              <section className="mb-12">
                <div className="bg-gradient-to-br from-[#0078D4] to-[#005A9E] text-white rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-4">Özet ve Sonraki Adımlar</h2>
                  <p className="text-white/90 mb-6">
                    AIR, modern SOC ekiplerinin vazgeçilmez bir aracıdır. Manuel soruşturma saatlerini
                    dakikalara indirerek güvenlik ekiplerinin stratejik tehditlere odaklanmasını sağlar.
                    Başarılı bir AIR dağıtımı için: önce Seviye 3 ile başlayın, Action Center&apos;ı
                    düzenli izleyin, false positive oranlarını ölçün ve olgunlaştıkça Seviye 5&apos;e
                    geçin. Kritik sunucular için ayrı cihaz grupları oluşturmayı unutmayın.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">2-5 dk</div>
                      <div className="text-white/80 text-sm">Ortalama AIR Yanıt Süresi</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">%95</div>
                      <div className="text-white/80 text-sm">Otomatik Düzeltme Oranı (Seviye 5)</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">7/24</div>
                      <div className="text-white/80 text-sm">Kesintisiz Otomatik Koruma</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* İlgili Makaleler */}
              <section className="mt-16 pt-12 border-t-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">İlgili Makaleler</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Link
                    to="/edr/defender-for-endpoint"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      Microsoft Defender for Endpoint Rehberi
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      EDR çözümünün tüm yeteneklerini, XDR entegrasyonunu ve lisans gereksinimlerini keşfedin
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>

                  <Link
                    to="/edr/asr-kurallari"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      ASR Kuralları ile Saldırı Yüzeyi Azaltma
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      AIR&apos;ı tamamlayan proaktif koruma katmanı: saldırıları başlamadan önce engelleyin
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>
                </div>
              </section>
            </div>
          </motion.article>
        </div>
      </section>

      {/* Alt Navigasyon */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium whitespace-nowrap">EDR Sayfası</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <span className="text-sm font-medium whitespace-nowrap">Ana Sayfa</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AirOtomatikYanitPage;
